import { useEffect, useState, Fragment } from "react";
import {
  fetchCustomerMasterByOrgId,
  downloadCustomerDataExcel,
} from "../Api/ViewCustomerMaster";
import { Container, Table, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import { useAuth } from "../Context/AuthContext";
import { useNavigate } from "react-router-dom";
import { getCustomerTypebyOrgId } from "../Api/CustomerMaster";
import { Modal, Form } from "react-bootstrap";
import { createCusVenCp } from "../Api/CusVenCpMasterApi";
import { fetchCusVenCpByCusVenType } from "../Api/CusVenCpMasterApi";
import { FaDownload } from "react-icons/fa6";
import "../Styling/AllTables.css"

const ViewCustomerMaster = () => {
  const [customers, setCustomers] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [customerTypes, setCustomerTypes] = useState([]);
  const { user } = useAuth();
  console.log("Authenticated user:", user);
  const navigate = useNavigate();

  const [showCpModal, setShowCpModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [expandedCustomerCode, setExpandedCustomerCode] = useState(null);
  const [contactPersonsMap, setContactPersonsMap] = useState({});
  const [loadingContacts, setLoadingContacts] = useState(false);

  const [cpForm, setCpForm] = useState({
    contactPerName: "",
    contactPerNumber: "",
    contactPerEmail: "",
  });

  const openCpModal = (customer) => {
    setSelectedCustomer(customer);
    setCpForm({
      contactPerName: "",
      contactPerNumber: "",
      contactPerEmail: "",
    });
    setShowCpModal(true);
  };

  const closeCpModal = () => {
    setShowCpModal(false);
    setSelectedCustomer(null);
  };

  const handleCpChange = (e) => {
    setCpForm({ ...cpForm, [e.target.name]: e.target.value });
  };

  const handleSaveContactPerson = async () => {
    try {
      if (!selectedCustomer) return;

      const payload = {
        cusVenTypeId: selectedCustomer.customerCode, // ✅ customer code
        contactPerName: cpForm.contactPerName,
        contactPerNumber: cpForm.contactPerNumber,
        contactPerEmail: cpForm.contactPerEmail,
        orgId: String(user.organisationId),
      };

      await createCusVenCp(payload, user.userId); // ✅ userId as userName

      toast.success("Contact person added successfully");
      closeCpModal();
    } catch (error) {
      console.error(error);
      toast.error("Failed to add contact person");
    }
  };

  const toggleContacts = async (customerCode) => {
    if (expandedCustomerCode === customerCode) {
      // collapse
      setExpandedCustomerCode(null);
      return;
    }

    setExpandedCustomerCode(customerCode);

    // already loaded → don’t refetch
    if (contactPersonsMap[customerCode]) return;

    try {
      setLoadingContacts(true);
      const data = await fetchCusVenCpByCusVenType(customerCode);
      setContactPersonsMap((prev) => ({
        ...prev,
        [customerCode]: data || [],
      }));
    } catch (error) {
      toast.error("Failed to load contact persons");
    } finally {
      setLoadingContacts(false);
    }
  };

  // ✅ Load both Customers and Customer Types
  useEffect(() => {
    const loadData = async () => {
      try {
        if (!user?.organisationId) return;

        const [customerData, typeData] = await Promise.all([
          fetchCustomerMasterByOrgId(user.organisationId),
          getCustomerTypebyOrgId(user.organisationId),
        ]);

        setCustomers(customerData || []);
        setCustomerTypes(typeData || []);
      } catch (error) {
        toast.error(`🦄 Error Fetching Customer Data`);
        console.error(error);
      }
    };

    loadData();
  }, [user]);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await downloadCustomerDataExcel();

      if (!response.data) {
        throw new Error("No data received from server");
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "CustomerMaster_Data.xlsx");

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        link.parentNode.removeChild(link);
      }, 100);

      toast.success("Customer data download started!");
    } catch (error) {
      console.error("Download error:", error);
      toast.error(`🦄 Download failed: ${error.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  // ✅ Helper to map code → description
  const getCustomerTypeDescription = (code) => {
    if (!code) return "";
    const type = customerTypes.find((t) => t.code === code);
    return type ? type.description : code; // fallback to code if not found
  };

  return (
    <>
      <div className="main-content expand">
        <Container fluid className="list-container">

          <div className="header-background">
          <div className="header-row"
          style={{
            display: 'flex',
            justifyContent: 'space-between', // push children to edges
            alignItems: 'center',            // vertical center
            width: '100%'
          }}
          >
            <span className="header-list-title">View Customer Master</span>
            <Button
              className="gradient-btn"
              onClick={handleDownload}
              disabled={isDownloading || customers.length === 0}
            >
              {isDownloading ? "Downloading..." : (
                <>
                  <FaDownload style={{ marginRight: "8px" }} />
                  Download Data
                </>
              )}
            </Button>
            </div>
          </div>

          {customers.length > 0 ? (
            <div className="table-wrapper">
              <div className="table-scroll-container">
                <Table bordered hover size="sm" className="data-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>#</th>
                      <th>Customer Code</th>
                      <th>Name</th>
                      <th>Contact Details</th>
                      <th>City</th>
                      <th>Country Code</th>
                      <th>Tax ID</th>
                      <th>Currency</th>
                      <th>Customer Type</th>
                      <th>Active</th>
                      <th>Short Names</th>
                      <th>Entered By</th>
                      <th>Entered Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((customer, index) => (
                      <Fragment key={customer.customerCode}>
                        {/* ===== MAIN CUSTOMER ROW ===== */}
                        <tr>
                          {/* Arrow column */}
                          <td style={{ width: "40px", textAlign: "center" }}>
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() =>
                                toggleContacts(customer.customerCode)
                              }
                            >
                              {expandedCustomerCode === customer.customerCode
                                ? "▼"
                                : "▶"}
                            </Button>
                          </td>

                          <td>{index + 1}</td>
                          <td>{customer.customerCode}</td>
                          <td>{customer.name}</td>
                          <td>{customer.mobileNumber}</td>
                          <td>{customer.cityName}</td>
                          <td>{customer.countryCode}</td>
                          <td>{customer.gstno ?? customer.taxId}</td>
                          <td>{customer.currencyCode}</td>
                          <td>
                            {getCustomerTypeDescription(customer.customerType)}
                          </td>
                          <td>{customer.active}</td>
                          <td>{customer.shortNames}</td>
                          <td>{customer.enteredBy}</td>
                          <td>{moment(customer.createdOnDate).format("L")}</td>

                          <td>
                            <div
                              style={{ display: "flex", flexDirection: "row" }}
                            >
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() =>
                                  navigate(
                                    `/editCustomer/${customer.customerCode}`
                                  )
                                }
                              >
                                Edit
                              </Button>

                              {/* <Button
                              variant="outline-success"
                              size="sm"
                              className="ms-2"
                              onClick={() => openCpModal(customer)}
                            >
                              👤
                            </Button> */}
                              <Button
                                variant="outline-info"
                                size="sm"
                                className="ms-2"
                                onClick={() =>
                                  navigate(
                                    `/viewCustomer/${customer.customerCode}`
                                  )
                                }
                              >
                                Info
                              </Button>
                            </div>
                            {/* existing buttons */}
                          </td>
                        </tr>

                        {/* ===== EXPANDED CONTACT PERSON ROW ===== */}
                        {expandedCustomerCode === customer.customerCode && (
                          <tr>
                            <td colSpan={15} style={{ background: "#f9f9f9" }}>
                              {loadingContacts ? (
                                <p className="m-2">
                                  Loading contact persons...
                                </p>
                              ) : (
                                <>
                                  {(contactPersonsMap[customer.customerCode]
                                    ?.length ?? 0) > 0 ? (
                                    <Table size="sm" bordered className="mb-0">
                                      <thead>
                                        <tr>
                                          <th>#</th>
                                          <th>Name</th>
                                          <th>Number</th>
                                          <th>Email</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {contactPersonsMap[
                                          customer.customerCode
                                        ].map((cp, i) => (
                                          <tr key={cp.cusVenId}>
                                            <td>{i + 1}</td>
                                            <td>{cp.contactPerName}</td>
                                            <td>{cp.contactPerNumber}</td>
                                            <td>{cp.contactPerEmail}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </Table>
                                  ) : (
                                    <p className="m-2 text-muted">
                                      No contact persons found.
                                    </p>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          ) : (
            <p className="no-data">No customer data found.</p>
          )}
        </Container>
        <Modal show={showCpModal} onHide={closeCpModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Add Contact Person</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Contact Person Name</Form.Label>
                <Form.Control
                  type="text"
                  name="contactPerName"
                  value={cpForm.contactPerName}
                  onChange={handleCpChange}
                  placeholder="Enter name"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Contact Person Number</Form.Label>
                <Form.Control
                  type="text"
                  name="contactPerNumber"
                  value={cpForm.contactPerNumber}
                  onChange={handleCpChange}
                  placeholder="Enter number"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Contact Person Email</Form.Label>
                <Form.Control
                  type="email"
                  name="contactPerEmail"
                  value={cpForm.contactPerEmail}
                  onChange={handleCpChange}
                  placeholder="Enter email"
                />
              </Form.Group>
            </Form>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={closeCpModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSaveContactPerson}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default ViewCustomerMaster;
